import { io, Socket } from 'socket.io-client';
import { Event, WebSocketMessage, ChatMessage } from '../types';
import { useAppStore } from '../stores/appStore';
import { toast } from 'sonner';
import i18n from '../i18n/config';

class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isManuallyDisconnected = false;

  constructor() {
    this.connect();
    this.setupEventListeners();
  }

  private connect(): void {
    try {
      const wsUrl = import.meta.env.VITE_WS_URL || window.location.origin;
      
      this.socket = io(wsUrl, {
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: false,
        reconnection: true,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: this.maxReconnectAttempts,
        auth: {
          token: localStorage.getItem('auth_token'),
        },
      });

      this.setupSocketEventListeners();
    } catch (error) {
      console.error('WebSocket connection error:', error);
      useAppStore.getState().setConnectionStatus('offline');
    }
  }

  private setupSocketEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      useAppStore.getState().setConnectionStatus('online');

      if (this.reconnectAttempts > 0) {
        toast.success(i18n.t('websocket.connectionRestored'));
      }
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);

      if (!this.isManuallyDisconnected) {
        useAppStore.getState().setConnectionStatus('offline');

        if (reason === 'io server disconnect') {
          // Server initiated disconnect - try to reconnect
          this.socket?.connect();
        }
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        useAppStore.getState().setConnectionStatus('offline');
        toast.error(i18n.t('websocket.connectionError'));
      } else {
        useAppStore.getState().setConnectionStatus('reconnecting');
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('WebSocket reconnected after', attemptNumber, 'attempts');
      useAppStore.getState().setConnectionStatus('online');
      toast.success(i18n.t('websocket.reconnected'));
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('WebSocket reconnection attempt:', attemptNumber);
      useAppStore.getState().setConnectionStatus('reconnecting');
    });

    this.socket.on('reconnect_failed', () => {
      console.log('WebSocket reconnection failed');
      useAppStore.getState().setConnectionStatus('offline');
      toast.error(i18n.t('websocket.reconnectFailed'));
    });

    // Application-specific events
    this.socket.on('event:updated', (event: Event) => {
      console.log('Event updated:', event);
      useAppStore.getState().updateEvent(event.id, event);
      
      // Show notification for active events
      if (event.status === 'active') {
        toast.info(i18n.t('websocket.eventStarted', { title: event.title }));
      }
    });

    this.socket.on('event:created', (event: Event) => {
      console.log('New event created:', event);
      useAppStore.getState().addEvent(event);
      toast.success(i18n.t('websocket.newEvent', { title: event.title }));
    });

    this.socket.on('event:deleted', (eventId: string) => {
      console.log('Event deleted:', eventId);
      useAppStore.getState().removeEvent(eventId);
      toast.info(i18n.t('websocket.eventCancelled'));
    });

    this.socket.on('event:reminder', (data: { eventId: string; message: string }) => {
      console.log('Event reminder:', data);
      toast.info(`🔔 ${data.message}`, {
        duration: 6000,
        action: {
          label: i18n.t('websocket.viewEvent'),
          onClick: () => {
            const event = useAppStore.getState().events.find(e => e.id === data.eventId);
            if (event) {
              useAppStore.getState().setCurrentEvent(event);
            }
          },
        },
      });
    });

    this.socket.on('chat:message', (message: ChatMessage) => {
      console.log('New chat message:', message);
      useAppStore.getState().addChatMessage(message);
      
      // Show notification if chat is closed
      const { isChatOpen } = useAppStore.getState();
      if (!isChatOpen && message.sender === 'acty') {
        toast.info(`💬 Acty: ${message.content.substring(0, 50)}...`, {
          action: {
            label: i18n.t('websocket.openChat'),
            onClick: () => useAppStore.getState().setChatOpen(true),
          },
        });
      }
    });

    this.socket.on('chat:typing', (isTyping: boolean) => {
      useAppStore.getState().setActyTyping(isTyping);
    });

    // System events
    this.socket.on('system:maintenance', (data: { message: string; scheduledFor: string }) => {
      toast.warning(i18n.t('websocket.scheduledMaintenance', { message: data.message }), {
        duration: 10000,
      });
    });

    this.socket.on('system:announcement', (data: { title: string; message: string; type: 'info' | 'warning' | 'success' }) => {
      toast[data.type](`📢 ${data.title}: ${data.message}`, {
        duration: 8000,
      });
    });
  }

  private setupEventListeners(): void {
    // Handle online/offline events
    window.addEventListener('online', () => {
      console.log('Browser came online');
      useAppStore.getState().setConnectionStatus('online');
      this.reconnect();
      toast.success(i18n.t('websocket.connectionRestored'));
    });

    window.addEventListener('offline', () => {
      console.log('Browser went offline');
      useAppStore.getState().setConnectionStatus('offline');
      toast.warning(i18n.t('websocket.offlineMode'));
    });

    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is hidden - reduce activity
        this.socket?.emit('user:away');
      } else {
        // Page is visible - resume normal activity
        this.socket?.emit('user:active');
        this.requestEventUpdates();
      }
    });
  }

  // Public methods
  public emit(event: string, data: any): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn('WebSocket not connected, cannot emit event:', event);
    }
  }

  public on(event: string, callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  public off(event: string, callback?: (data: any) => void): void {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  public disconnect(): void {
    this.isManuallyDisconnected = true;
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  public reconnect(): void {
    this.isManuallyDisconnected = false;
    if (this.socket) {
      if (!this.socket.connected) {
        this.socket.connect();
      }
    } else {
      this.connect();
    }
  }

  public isConnected(): boolean {
    return this.socket ? this.socket.connected : false;
  }

  // Event-specific methods
  public joinEventRoom(eventId: string): void {
    this.emit('event:join', { eventId });
  }

  public leaveEventRoom(eventId: string): void {
    this.emit('event:leave', { eventId });
  }

  public sendChatMessage(message: string): void {
    this.emit('chat:message', {
      message,
      timestamp: new Date().toISOString(),
    });
  }

  public startTyping(): void {
    this.emit('chat:typing', true);
  }

  public stopTyping(): void {
    this.emit('chat:typing', false);
  }

  public requestEventUpdates(): void {
    this.emit('events:request_updates');
  }

  public setUserActive(): void {
    this.emit('user:active');
  }

  public setUserAway(): void {
    this.emit('user:away');
  }

  // Utility methods
  public getConnectionState(): 'connected' | 'disconnected' | 'connecting' {
    if (!this.socket) return 'disconnected';
    if (this.socket.connected) return 'connected';
    if (this.socket.connecting) return 'connecting';
    return 'disconnected';
  }
}

// Create singleton instance
const wsService = new WebSocketService();

export default wsService;

// Export for manual usage in components
export { WebSocketService };